// =============================================================================
// FILE: packages/renderer/src/lib/masterProposal.ts
// PURPOSE: "Master Proposal" bundle. Assembles the Strategy Summary, Manning
//   Report, Certificate of Compliance and Cable Certification Summary into a
//   single high-end, client-ready PDF. All narrative text is run through the
//   Technical Proofreader auto-polish before rendering.
// =============================================================================

import { jsPDF } from 'jspdf';
import { proofread } from './technicalProofreader.js';
import type { MultiTradeResult } from './contextAnalyzer.js';
import type { ManningReport } from './laborAgent.js';
import type { AuditResult, CableTestResult } from './standardsAuditor.js';
import { REGIONS } from './standardsAuditor.js';

const PAGE_W = 1754;
const PAGE_H = 1240;

export interface ProposalInputs {
  project: string;
  author: string;
  strategy: string;
  trade?: MultiTradeResult;
  manning: ManningReport;
  audit: AuditResult;
  cable?: CableTestResult;
  priceUsd: number;
}

export interface ProposalResult { blobUrl: string; fileName: string; firstPageDataUrl: string }

function esc(s: string): string {
  return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function frame(inner: string, footer: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PAGE_W}" height="${PAGE_H}" viewBox="0 0 ${PAGE_W} ${PAGE_H}">
    <defs><linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#070b14"/><stop offset="100%" stop-color="#0b1220"/></linearGradient>
    <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse"><path d="M 48 0 L 0 0 0 48" fill="none" stroke="rgba(40,68,116,0.14)" stroke-width="1"/></pattern></defs>
    <rect width="${PAGE_W}" height="${PAGE_H}" fill="url(#bg)"/><rect width="${PAGE_W}" height="${PAGE_H}" fill="url(#grid)"/>
    <rect x="40" y="40" width="${PAGE_W - 80}" height="${PAGE_H - 80}" rx="18" fill="none" stroke="rgba(37,181,168,0.28)" stroke-width="2"/>
    ${inner}
    <g transform="translate(${PAGE_W - 130},${PAGE_H - 130})" opacity="0.96">
      <circle cx="0" cy="0" r="86" fill="none" stroke="#25B5A8" stroke-width="2.5" opacity="0.55"/>
      <text x="0" y="-6" text-anchor="middle" font-family="Inter,sans-serif" font-weight="900" font-size="46" fill="#E0FFFE">R²</text>
      <text x="0" y="34" text-anchor="middle" font-family="Inter,sans-serif" font-weight="800" font-size="15" letter-spacing="2" fill="#5EEAD4">CERTIFIED</text>
      <text x="0" y="54" text-anchor="middle" font-family="Inter,sans-serif" font-weight="700" font-size="11" letter-spacing="3" fill="rgba(148,172,210,0.85)">BY RCD²</text>
    </g>
    <text x="88" y="${PAGE_H - 70}" font-family="Inter,sans-serif" font-size="20" fill="rgba(148,172,210,0.7)" letter-spacing="1.5">${esc(footer)}</text>
  </svg>`;
}

/** Wrap a long paragraph into <tspan> lines. */
function wrap(text: string, x: number, y: number, size: number, lineH: number, perLine: number, fill: string): string {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    if ((cur + ' ' + w).trim().length > perLine) { lines.push(cur.trim()); cur = w; } else cur += ' ' + w;
  }
  if (cur.trim()) lines.push(cur.trim());
  return `<text x="${x}" y="${y}" font-family="Inter,sans-serif" font-size="${size}" fill="${fill}">` +
    lines.map((l, i) => `<tspan x="${x}" dy="${i === 0 ? 0 : lineH}">${esc(l)}</tspan>`).join('') + `</text>`;
}

function coverSvg(p: ProposalInputs, dateStr: string): string {
  const inner = `
    <text x="140" y="150" font-family="Inter,sans-serif" font-size="26" fill="rgba(37,181,168,0.85)" letter-spacing="6">RCD² PROJECT CERTIFICATION PASS</text>
    <text x="140" y="270" font-family="Inter,sans-serif" font-weight="800" font-size="94" fill="#F0F5FF" letter-spacing="-1">Master Proposal</text>
    <text x="144" y="330" font-family="Inter,sans-serif" font-weight="600" font-size="30" fill="#5EEAD4" letter-spacing="4">STRATEGY · MANNING · COMPLIANCE · CERTIFICATION</text>
    <rect x="140" y="370" width="${PAGE_W - 280}" height="2" fill="rgba(37,181,168,0.4)"/>
    <text x="140" y="460" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)" letter-spacing="3">PROJECT</text>
    <text x="140" y="508" font-family="Inter,sans-serif" font-weight="700" font-size="44" fill="#E2E8F0">${esc(p.project)}</text>
    <text x="140" y="590" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)" letter-spacing="3">ENGINEER OF RECORD</text>
    <text x="140" y="636" font-family="Inter,sans-serif" font-weight="700" font-size="38" fill="#E2E8F0">${esc(p.author)}</text>
    <text x="140" y="716" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)" letter-spacing="3">REGION / STANDARDS</text>
    <text x="140" y="762" font-family="Inter,sans-serif" font-weight="700" font-size="32" fill="#E2E8F0">${esc(REGIONS[p.audit.region].label)}</text>
    <g transform="translate(${PAGE_W - 560},440)">
      <rect x="0" y="0" width="420" height="150" rx="14" fill="${p.audit.compliant ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.1)'}" stroke="${p.audit.compliant ? '#34D399' : '#F87171'}" stroke-width="2"/>
      <text x="30" y="46" font-family="Inter,sans-serif" font-size="22" fill="rgba(148,172,210,0.8)" letter-spacing="2">COMPLIANCE STATUS</text>
      <text x="30" y="112" font-family="Inter,sans-serif" font-weight="800" font-size="52" fill="${p.audit.compliant ? '#5EEAD4' : '#FCA5A5'}">${p.audit.compliant ? 'VERIFIED' : 'REVIEW'}</text>
    </g>
    <text x="140" y="${PAGE_H - 120}" font-family="Inter,sans-serif" font-size="22" fill="rgba(148,172,210,0.65)">Issued ${esc(dateStr)} · Auto-Polished by the RCD² Technical Proofreader</text>
  `;
  return frame(inner, 'RCD² MASTER PROPOSAL · CONFIDENTIAL · PAGE 1 / 4');
}

function strategySvg(p: ProposalInputs): string {
  const trades = p.trade?.packages.filter((t) => t.detected) ?? [];
  const tradeRows = trades.map((t, i) => `
    <text x="140" y="${560 + i * 46}" font-family="Inter,sans-serif" font-size="24" fill="#E2E8F0">${esc(t.label)}</text>
    <text x="900" y="${560 + i * 46}" text-anchor="end" font-family="Inter,sans-serif" font-weight="700" font-size="24" fill="#5EEAD4">$${t.estimateUsd.toLocaleString()}</text>`).join('');
  const m = p.manning;
  const inner = `
    <text x="140" y="150" font-family="Inter,sans-serif" font-size="26" fill="rgba(37,181,168,0.8)" letter-spacing="6">EXECUTIVE STRATEGY</text>
    <text x="140" y="220" font-family="Inter,sans-serif" font-weight="800" font-size="58" fill="#F0F5FF">Strategy &amp; Manning</text>
    ${wrap(proofread(p.strategy).text, 140, 300, 24, 34, 78, 'rgba(226,232,240,0.9)')}
    <text x="140" y="510" font-family="Inter,sans-serif" font-size="22" fill="rgba(148,172,210,0.7)" letter-spacing="3">DESIGN FEE BY TRADE</text>
    ${tradeRows}
    <g transform="translate(1080,470)">
      ${[
        ['Total Man-Hours', m.totalHours.toLocaleString()],
        ['Recommended Crew', `${m.crewSize} techs`],
        ['Duration', `${m.durationDays} days (${m.durationWeeks} wks)`],
        ['Daily Labor Burn', `${m.dailyBurnHours} hrs/day`],
      ].map(([k, v], i) => `
        <rect x="0" y="${i * 88}" width="480" height="72" rx="12" fill="rgba(14,20,38,0.85)" stroke="rgba(37,181,168,0.3)" stroke-width="1.5"/>
        <text x="22" y="${i * 88 + 30}" font-family="Inter,sans-serif" font-size="18" fill="rgba(148,172,210,0.7)" letter-spacing="1">${k}</text>
        <text x="460" y="${i * 88 + 52}" text-anchor="end" font-family="Inter,sans-serif" font-weight="800" font-size="30" fill="#5EEAD4">${v}</text>`).join('')}
    </g>`;
  return frame(inner, 'STRATEGY & MANNING REPORT · PAGE 2 / 4');
}

function complianceSvg(p: ProposalInputs): string {
  const rows = p.audit.checks.map((c, i) => {
    const y = 360 + i * 118;
    const color = c.exception ? '#FBBF24' : c.status === 'pass' ? '#34D399' : c.status === 'warning' ? '#FBBF24' : '#F87171';
    const verdict = c.exception ? 'EXCEPTION' : c.status.toUpperCase();
    return `
      <text x="140" y="${y}" font-family="Inter,sans-serif" font-weight="700" font-size="26" fill="#E2E8F0">${esc(c.title)}</text>
      <text x="140" y="${y + 30}" font-family="Inter,sans-serif" font-size="20" fill="rgba(148,172,210,0.7)" letter-spacing="1">${esc(c.code)}${c.measured ? ` · measured ${esc(c.measured)} / limit ${esc(c.limit ?? '')}` : ''}</text>
      <text x="${PAGE_W - 140}" y="${y}" text-anchor="end" font-family="Inter,sans-serif" font-weight="800" font-size="24" fill="${color}">${verdict}</text>
      ${c.exception ? wrap(proofread(c.exception).text, 140, y + 60, 16, 22, 120, 'rgba(251,191,36,0.85)') : ''}
      <rect x="140" y="${y + 44}" width="${PAGE_W - 280}" height="1" fill="rgba(40,68,116,0.4)"/>`;
  }).join('');
  const inner = `
    <text x="140" y="150" font-family="Inter,sans-serif" font-size="26" fill="rgba(37,181,168,0.8)" letter-spacing="6">CERTIFICATE OF COMPLIANCE</text>
    <text x="140" y="220" font-family="Inter,sans-serif" font-weight="800" font-size="58" fill="#F0F5FF">Standards Verified</text>
    <text x="140" y="270" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)">${esc(REGIONS[p.audit.region].body)} · ${p.audit.passed} passed · ${p.audit.violations} violation(s) · ${p.audit.warnings} warning(s)</text>
    ${rows}`;
  return frame(inner, 'CERTIFICATE OF COMPLIANCE · PAGE 3 / 4');
}

function cableSvg(p: ProposalInputs): string {
  const c = p.cable;
  const body = c && c.total > 0
    ? `<g transform="translate(140,360)">
        ${([['Tested', String(c.total)], ['Passed', String(c.passed)], ['Failed', String(c.failed)]] as Array<[string, string]>).map(([k, v], i) => `
          <rect x="${i * 400}" y="0" width="360" height="120" rx="14" fill="rgba(14,20,38,0.85)" stroke="rgba(37,181,168,0.3)" stroke-width="1.5"/>
          <text x="${i * 400 + 24}" y="44" font-family="Inter,sans-serif" font-size="20" fill="rgba(148,172,210,0.7)" letter-spacing="2">${k.toUpperCase()}</text>
          <text x="${i * 400 + 24}" y="100" font-family="Inter,sans-serif" font-weight="800" font-size="48" fill="${k === 'Failed' ? (c.failed > 0 ? '#F87171' : '#34D399') : '#5EEAD4'}">${v}</text>`).join('')}
      </g>
      ${c.failed > 0 ? `<text x="140" y="560" font-family="Inter,sans-serif" font-size="24" fill="#FCA5A5">Failed IDs requiring re-termination:</text>
        ${wrap(c.rows.filter((r) => r.verdict === 'violation').map((r) => `${r.id} (${r.lengthM} m)`).join(', ') || 'None', 140, 610, 22, 30, 84, 'rgba(226,232,240,0.85)')}`
        : `<text x="140" y="560" font-family="Inter,sans-serif" font-size="26" fill="#5EEAD4">All tested links pass against the project standard — ready for close-out.</text>`}`
    : `<text x="140" y="380" font-family="Inter,sans-serif" font-size="26" fill="rgba(148,172,210,0.7)">No cable-test data ingested. Attach Fluke / LinkWare CSV results to include a certification summary.</text>`;
  const inner = `
    <text x="140" y="150" font-family="Inter,sans-serif" font-size="26" fill="rgba(37,181,168,0.8)" letter-spacing="6">CABLE CERTIFICATION SUMMARY</text>
    <text x="140" y="220" font-family="Inter,sans-serif" font-weight="800" font-size="58" fill="#F0F5FF">Test Validation</text>
    <text x="140" y="270" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)">Validated against 100 m channel · NEXT ≥ 35 dB · Return Loss ≥ 15 dB</text>
    ${body}`;
  return frame(inner, 'CABLE CERTIFICATION SUMMARY · PAGE 4 / 4');
}

function rasterize(svg: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = PAGE_W; canvas.height = PAGE_H;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#070b14'; ctx.fillRect(0, 0, PAGE_W, PAGE_H);
      ctx.drawImage(img, 0, 0, PAGE_W, PAGE_H);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function generateMasterProposal(
  p: ProposalInputs,
  onProgress?: (dataUrl: string, i: number) => void,
): Promise<ProposalResult> {
  const dateStr = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  const svgs = [coverSvg(p, dateStr), strategySvg(p), complianceSvg(p), cableSvg(p)];
  const pages: string[] = [];
  const names = ['cover', 'strategy', 'compliance', 'cable'];
  for (let i = 0; i < svgs.length; i++) {
    let dataUrl: string;
    try {
      dataUrl = await rasterize(svgs[i]!);
    } catch {
      throw new Error(`page "${names[i]}" failed to rasterize`);
    }
    pages.push(dataUrl);
    onProgress?.(dataUrl, i);
  }
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  pages.forEach((url, i) => { if (i > 0) pdf.addPage(); pdf.addImage(url, 'PNG', 0, 0, pw, ph, undefined, 'FAST'); });
  const fileName = `RCD2-Master-Proposal-${p.project.replace(/[^a-z0-9]+/gi, '-')}-${Date.now()}.pdf`;
  const blobUrl = URL.createObjectURL(pdf.output('blob'));
  return { blobUrl, fileName, firstPageDataUrl: pages[0]! };
}
